"use client";

import React, { useEffect, useMemo } from "react";
import { create } from "zustand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui";
import { Button } from "@repo/ui";

type UnsavedStore = {
  // state
  dirty: boolean;
  open: boolean;
  resolver: ((v: boolean) => void) | null;

  // actions
  setDirty: (v: boolean) => void;
  setOpen: (v: boolean) => void;
  setResolver: (r: UnsavedStore["resolver"]) => void;

  confirmNavigate: () => Promise<boolean>;
  onCancel: () => void;
  onConfirm: () => void;
};

const useUnsavedStore = create<UnsavedStore>((set, get) => ({
  dirty: false,
  open: false,
  resolver: null,

  setDirty: (v) => set({ dirty: v }),
  setOpen: (v) => set({ open: v }),
  setResolver: (r) => set({ resolver: r }),

  confirmNavigate: async () => {
    if (!get().dirty) return true;
    set({ open: true });
    return new Promise<boolean>((resolve) => {
      set({ resolver: resolve });
    });
  },

  onCancel: () => {
    const { resolver } = get();
    set({ open: false, resolver: null });
    resolver?.(false);
  },

  onConfirm: () => {
    const { resolver } = get();
    // User confirmed leaving: mark clean so guards are removed
    set({ dirty: false, open: false, resolver: null });
    resolver?.(true);
  },
}));

export function useUnsavedChanges() {
  const dirty = useUnsavedStore((s) => s.dirty);
  const setDirty = useUnsavedStore((s) => s.setDirty);
  const confirmNavigate = useUnsavedStore((s) => s.confirmNavigate);

  // Return stable reference for the hook result
  return useMemo(() => ({ dirty, setDirty, confirmNavigate }), [dirty, setDirty, confirmNavigate]);
}

type ProviderProps = {
  children: React.ReactNode;
  confirmTitle?: string;
  confirmMessage?: string;
  cancelLabel?: string;
  confirmLabel?: string;
};

export function UnsavedChangesProvider({
  children,
  confirmTitle = "Unsaved changes",
  confirmMessage = "Do you want to discard the changes and leave this page?",
  cancelLabel = "Stay",
  confirmLabel = "Discard changes",
}: ProviderProps) {
  return (
    <>
      {children}
      <UnsavedChangesRoot
        confirmTitle={confirmTitle}
        confirmMessage={confirmMessage}
        cancelLabel={cancelLabel}
        confirmLabel={confirmLabel}
      />
    </>
  );
}

function UnsavedChangesRoot({
  confirmTitle,
  confirmMessage,
  cancelLabel,
  confirmLabel,
}: {
  confirmTitle: string;
  confirmMessage: string;
  cancelLabel: string;
  confirmLabel: string;
}) {
  const open = useUnsavedStore((s) => s.open);
  const onCancel = useUnsavedStore((s) => s.onCancel);
  const onConfirm = useUnsavedStore((s) => s.onConfirm);

  const dirty = useUnsavedStore((s) => s.dirty);
  const confirmNavigate = useUnsavedStore((s) => s.confirmNavigate);
  const setOpen = useUnsavedStore((s) => s.setOpen);

  // Warn on refresh/close/tab close (cannot show custom modal here)
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Guard browser back/forward while dirty
  useEffect(() => {
    if (!dirty) return;

    const onPopState = async () => {
      const ok = await confirmNavigate();
      if (!ok) {
        // Revert navigation by restoring current URL
        history.pushState(null, "", location.href);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [dirty, confirmNavigate]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // If the user dismisses via backdrop/esc, treat as cancel
        if (!next && open) onCancel();
        else setOpen(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{confirmTitle}</DialogTitle>
          <DialogDescription>{confirmMessage}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onConfirm}>
            {confirmLabel}
          </Button>
          <Button autoFocus onClick={onCancel} variant={"secondary"}>
            {cancelLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
